import {
  type DocumentSearchMeta,
  isDocumentSearchVersion,
} from "@prismicio/editor-fields"
import { range, sort } from "@prismicio/editor-support/Array"
import {
  getMinutesFrom,
  getMonthsFrom,
  getRelativeTimeString,
} from "@prismicio/editor-support/Date"
import { escapeStringRegexp } from "@prismicio/editor-support/String"
import {
  Badge,
  BlankSlate,
  BlankSlateDescription,
  BlankSlateIcon,
  BlankSlateTitle,
  Box,
  ComboBoxItem,
  ErrorBoundary,
  Icon,
  ProgressCircle,
  ScrollArea,
  Separator,
  Skeleton,
  Text,
} from "@prismicio/editor-ui"
import { type Dispatch, type SetStateAction } from "react"
import { Link, useNavigate } from "react-router-dom"

import type { DocumentVersionStatus } from "../../app/services/core"
import { useCustomType } from "../../app/services/customTypes"
import { trackSelectDocumentInDialog } from "../../app/services/tracking"
import {
  useCustomTypesFilters,
  useRepositoryAuthorsFilters,
  useRepositoryTagsFilters,
} from "../DocumentListBody/ActionBar/ActionBar"
import { FilterBar } from "../DocumentListBody/ActionBar/FilterBar"
import {
  type Filter,
  FilterDropDown,
  FilterDropDownBoundary,
  type OptionsFetcherItem,
} from "../DocumentListBody/ActionBar/FilterDropdown"
import { useSearchDialogContext } from "./useDocumentSearch"

interface DocumentSearchContentProps {
  selectedFilters: Filter[]
  setSelectedFilters: Dispatch<SetStateAction<Filter[]>>
}

export function DocumentSearchContent(props: DocumentSearchContentProps) {
  const { selectedFilters, setSelectedFilters } = props

  return (
    <>
      <ActionBar
        selectedFilters={selectedFilters}
        setSelectedFilters={setSelectedFilters}
      />

      <Separator />

      <Box flexDirection="column" overflowY="auto" height="350px">
        <DocumentList />
      </Box>
    </>
  )
}

function DocumentList() {
  const { filters, loading, documents } = useSearchDialogContext()
  const hasNoDocuments = documents.length === 0

  const trackDocumentSelection = (
    selectedDocumentPosition: number,
    totalDocuments: number,
  ) => {
    trackSelectDocumentInDialog({
      numberOfResults: totalDocuments,
      numberOfWords: filters.searchTerm.split(" ").length,
      // since array position could be 0, we want to display 1 as it's more user friendly
      searchPosition: selectedDocumentPosition + 1,
      searchQuery: filters.searchTerm,
    })
  }

  const navigate = useNavigate()

  // No search filters applied yet
  if (hasNoDocuments && !filters.hasFiltersApplied) {
    return (
      <NoDocumentsPlaceholder
        title="Search for pages"
        subtitle="Enter keywords or apply filters to find the pages you need."
      />
    )
  }

  if (hasNoDocuments && !loading) {
    return (
      <NoDocumentsPlaceholder
        title="No pages found"
        subtitle="Consider using different keywords or tweaking your filters to find what you need."
        variant="error"
      />
    )
  }

  const showSearchSkeleton = hasNoDocuments && loading
  const showProgressCircle = loading && !showSearchSkeleton

  return (
    <ScrollArea style={{ paddingLeft: 12, paddingRight: 1, paddingBottom: 12 }}>
      <Box padding={{ top: 10, bottom: 4 }} gap={8}>
        <Text variant="smallBold" color="grey11">
          Pages
        </Text>
        {showProgressCircle && <ProgressCircle color="grey11" />}
      </Box>

      {showSearchSkeleton ? (
        <SearchSkeletonLoader />
      ) : (
        documents.map((document, index) => (
          <Link
            onClick={() => trackDocumentSelection(index, documents.length)}
            to={`/pages/${document.id}`}
            key={document.id}
            style={{ textDecoration: "none" }}
          >
            <ComboBoxItem
              value={document.id}
              onCheckedChange={() => {
                navigate(`/pages/${document.id}`)
              }}
              variant="action-list-item"
            >
              <Box
                display="grid"
                gridTemplateColumns="min-content 1fr max-content"
                width="100%"
              >
                <Icon
                  name="description"
                  size="small"
                  color="grey11"
                  sx={{ marginRight: 8, alignSelf: "start", marginTop: 4 }}
                />
                <Box flexWrap="wrap" flexGrow={1} flexBasis={0}>
                  <Text>
                    <HighlightedText
                      text={document.title}
                      highlight={filters.searchTerm}
                    />
                    <ErrorBoundary>
                      <CustomTypeName id={document.custom_type_id} />
                    </ErrorBoundary>
                  </Text>
                  {filters.status.length > 0 && (
                    <VersionStatusBadge versions={document.versions} />
                  )}
                </Box>
                <DocumentDate document={document} />

                <DocumentUIDSearchResult
                  document={document}
                  searchTerm={filters.searchTerm}
                />
              </Box>
            </ComboBoxItem>
          </Link>
        ))
      )}
    </ScrollArea>
  )
}

interface ActionBarProps {
  selectedFilters: Filter[]
  setSelectedFilters: Dispatch<SetStateAction<Filter[]>>
}

function ActionBar(props: ActionBarProps) {
  const { selectedFilters, setSelectedFilters } = props

  const {
    filters,
    tagFilterDropdownRef,
    typeFilterDropdownRef,
    statusFilterDropdownRef,
    authorFilterDropdownRef,
  } = useSearchDialogContext()

  function onRemoveFilter(filter: Filter) {
    const newValues = selectedFilters?.filter(
      (otherFilter) =>
        otherFilter.value !== filter?.value || otherFilter.type !== filter.type,
    )

    setSelectedFilters(newValues)
  }

  function onFilterSelected(filter: Filter) {
    const alreadySelected = selectedFilters?.some(
      (type) => type.value === filter.value && type.type === filter.type,
    )

    if (alreadySelected) return onRemoveFilter(filter)

    const newFilters = [...selectedFilters, filter]

    setSelectedFilters(newFilters)
  }

  return (
    <Box flexDirection="column" gap={8} padding={{ inline: 12, block: 8 }}>
      <Box alignItems="center" gap={16}>
        <FilterDropDownBoundary
          type="customType"
          selectedFilters={filters.customType}
        >
          <FilterDropDown
            type="customType"
            optionsFetcher={useCustomTypesFilters}
            selectedFilters={filters.customType}
            onFilterSelected={onFilterSelected}
            ref={typeFilterDropdownRef}
          />
        </FilterDropDownBoundary>
        <FilterDropDownBoundary type="tag" selectedFilters={filters.tag}>
          <FilterDropDown
            type="tag"
            optionsFetcher={useRepositoryTagsFilters}
            selectedFilters={filters.tag}
            onFilterSelected={onFilterSelected}
            ref={tagFilterDropdownRef}
          />
        </FilterDropDownBoundary>
        <FilterDropDown
          type="status"
          optionsFetcher={() =>
            [
              { id: "unclassified", label: "Draft", key: "unclassified" },
              { id: "published", label: "Published", key: "published" },
              { id: "release", label: "Release", key: "release" },
              { id: "archived", label: "Archived", key: "archived" },
            ] satisfies OptionsFetcherItem<DocumentVersionStatus>[]
          }
          selectedFilters={filters.status}
          onFilterSelected={onFilterSelected}
          ref={statusFilterDropdownRef}
        />
        <FilterDropDownBoundary type="author" selectedFilters={filters.authors}>
          <FilterDropDown
            type="author"
            optionsFetcher={useRepositoryAuthorsFilters}
            selectedFilters={filters.authors}
            onFilterSelected={onFilterSelected}
            ref={authorFilterDropdownRef}
          />
        </FilterDropDownBoundary>
      </Box>

      {selectedFilters.length > 0 && (
        <FilterBar
          selectedFilters={selectedFilters}
          onRemoveFilter={onRemoveFilter}
        />
      )}
    </Box>
  )
}

function SearchSkeletonLoader() {
  return (
    <>
      {range(1, 8).map((num) => (
        <ComboBoxItem
          key={num}
          value={String(num)}
          disabled
          variant="action-list-item"
        >
          <Icon
            name="description"
            size="small"
            color="grey11"
            sx={{ marginRight: 8, alignSelf: "start", marginTop: 4 }}
          />
          <Skeleton
            key={num}
            height={16}
            sx={{ flexGrow: 1, marginBlock: 4 }}
            borderRadius={12}
          />
        </ComboBoxItem>
      ))}
    </>
  )
}

interface NoDocumentsPlaceholderProps {
  title: string
  subtitle?: string
  variant?: NoDocumentsPlaceholderVariant
}

type NoDocumentsPlaceholderVariant = "default" | "error"

const noDocumentsPlaceholderVariants = {
  default: {
    icon: { name: "search", lineColor: undefined, backgroundColor: "grey4" },
  },
  error: {
    icon: { name: "close", lineColor: "tomato11", backgroundColor: "tomato4" },
  },
} as const

function NoDocumentsPlaceholder(props: NoDocumentsPlaceholderProps) {
  const { title, subtitle, variant = "default" } = props

  const { icon } = noDocumentsPlaceholderVariants[variant]

  return (
    <BlankSlate sx={{ marginTop: 72 }}>
      <BlankSlateIcon
        name={icon.name}
        lineColor={icon.lineColor}
        backgroundColor={icon.backgroundColor}
        size="large"
      />
      <BlankSlateTitle size="big">{title}</BlankSlateTitle>
      <BlankSlateDescription width={420}>{subtitle}</BlankSlateDescription>
    </BlankSlate>
  )
}

interface HighlightedTextProps {
  text: string
  highlight: string
  variant?: "title" | "uid"
}

function HighlightedText(props: HighlightedTextProps) {
  const { text, highlight, variant = "title" } = props
  const config = highlightedTextVariantConfig[variant]

  const parts = highlight
    ? text.split(new RegExp(`(${escapeStringRegexp(highlight)})`, "gi"))
    : [text]

  return (
    <Text
      variant={config.textVariants.text}
      color={config.colors.text}
      hyphens
      overflowWrap="anywhere"
    >
      {parts.map((part, index) =>
        part.toLowerCase() === highlight.toLowerCase() ? (
          <Text
            variant={config.textVariants.highlight}
            key={index}
            hyphens
            overflowWrap="anywhere"
            highlight
            highlightColor={config.colors.highlight}
            highlightBorderRadius={2}
            color={config.colors.highlightText}
          >
            {part}
          </Text>
        ) : (
          part
        ),
      )}
    </Text>
  )
}

const highlightedTextVariantConfig = {
  title: {
    colors: {
      text: "grey11",
      highlight: undefined,
      highlightText: "purple11",
    },
    textVariants: {
      text: "normal",
      highlight: "bold",
    },
  },
  uid: {
    colors: {
      text: "green11",
      highlight: "green3",
      highlightText: "green11",
    },
    textVariants: {
      text: "extraSmall",
      highlight: "extraSmall",
    },
  },
} as const

interface CustomTypeNameProps {
  id: string
}

function CustomTypeName(props: CustomTypeNameProps) {
  const { id } = props

  const customType = useCustomType(id)

  return (
    <Text variant="small" color="grey11" hyphens overflowWrap="anywhere">
      {` — ${customType.label}`}
    </Text>
  )
}

interface VersionStatusBadgeProps {
  versions: DocumentSearchMeta["versions"]
}

function VersionStatusBadge(props: VersionStatusBadgeProps) {
  const { versions } = props

  // If all versions are archived, show the archived badge only
  // we don't have to do expensive sorting and filtering, as we only have one status
  if (versions.every((version) => version.status === "archived"))
    return <Badge sx={{ marginLeft: 8 }} title="In: Archived" />

  // Sort versions by status priority and remove archived versions
  // we want to only show archived badges if a document is fully archived
  const sortedVersions = sort(
    versions.filter((version) => version.status !== "archived"),
    (version) => statusDetails[version.status]?.priority ?? 0,
  )
  const sortedLabels = sortedVersions.map(
    (version) => statusDetails[version.status]?.label,
  )

  const statuses = [...new Set(sortedLabels)].join(" & ")

  return (
    <Badge sx={{ marginLeft: 8 }} size="medium" title={`In: ${statuses}`} />
  )
}

export const statusDetails: Record<
  string,
  { label: string; priority: number }
> = {
  unclassified: {
    label: "Draft",
    priority: 1,
  },
  published: {
    label: "Published",
    priority: 2,
  },
  release: {
    label: "Release",
    priority: 3,
  },
  archived: {
    label: "Archived",
    priority: 4,
  },
}

interface DocumentDateProps {
  document: DocumentSearchMeta
}

function DocumentDate(props: DocumentDateProps) {
  const { document } = props

  const lastModifiedDate = document.versions?.[0]?.last_modified_date

  if (!lastModifiedDate) return null

  return (
    <Text
      color="grey10"
      variant="small"
      sx={{
        marginLeft: 8,
        marginTop: 4,
        alignSelf: "start",
        justifySelf: "end",
      }}
    >
      {getReadableDate(lastModifiedDate)}
    </Text>
  )
}

/**
 * Instead of using the generally available `getDateDiffString` function, we're
 * using a custom implementation here as the search results have less screen
 * real estate, so we display the date in a more concise way.
 */
function getReadableDate(date: Date) {
  const minutes = getMinutesFrom(date)
  const months = getMonthsFrom(date)

  // anything outside the current year should be displayed as a full date "Jan 1, 2021"
  if (months > 12)
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    })

  // anything outside the current month should be displayed as month + date "Jan 1"
  if (months > 0)
    return date.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
    })

  // anything outside the current minute should be displayed relative to the
  // current time e.g. "1m ago", "1h ago", "1d ago", "1w ago"
  if (minutes > 0) return getRelativeTimeString({ date, style: "narrow" })

  // anything within the current minute should be displayed as "Just now"
  return "Just now"
}

interface DocumentUIDSearchResultProps {
  document: DocumentSearchMeta
  searchTerm: string
}

function DocumentUIDSearchResult(props: DocumentUIDSearchResultProps) {
  const { searchTerm, document } = props

  const sortedVersions = sort(
    document.versions.filter((version) => version.status !== "archived"),
    (version) => statusDetails[version.status]?.priority ?? 0,
  )

  const version = sortedVersions[0]
  if (!isDocumentSearchVersion(version)) return null

  const highlightedUID = version.highlights?.uid
  if (!highlightedUID) return null

  return (
    <Box gridColumn="2" alignItems="flex-start">
      <Icon name="tag" color="green11" sx={{ width: 12, height: 12 }} />
      <HighlightedText
        variant="uid"
        highlight={searchTerm}
        text={highlightedUID}
      />
    </Box>
  )
}
